/**
 * The live queue: every page parked for a person right now, across all users.
 *
 * One query feeds the nav badge, the banner, the overview and the queue page.
 * The server is asked every few seconds; between answers the clocks tick
 * locally, so a countdown never sits still.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { adminApi } from '@/api/endpoints'
import type { Tone } from '@/components/ui/StatusPill'
import type { LiveItem } from '@/types/api'

export const LIVE_KEY = ['live'] as const
const POLL_MS = 3000

/** What the page is asking a person to do, in the words the queue uses. */
export const REASON: Record<string, { label: string; ask: string }> = {
  captcha: { label: 'CAPTCHA', ask: 'Solve the CAPTCHA on the page.' },
  human: { label: 'Human check', ask: 'Finish the "are you human" check on the page.' },
  sms: { label: 'Phone code', ask: 'This needs a code sent to the user\'s phone. Only the user can finish it.' },
}
export const reasonOf = (reason: string | null | undefined) => REASON[reason ?? ''] ?? { label: 'Check', ask: 'Finish the check on the page.' }

export function hostOf(url: string | null | undefined): string {
  if (!url) return ''
  try {
    return new URL(url).host.replace(/^www\./, '')
  } catch {
    return ''
  }
}

/** `95` → `1:35`. */
export function clock(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '–'
  const s = Math.max(0, Math.round(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** How loudly the time left should read. */
export function urgency(remaining: number | null | undefined): Tone {
  if (remaining === null || remaining === undefined) return 'info'
  if (remaining <= 30) return 'danger'
  if (remaining <= 75) return 'warning'
  return 'info'
}

/** What a queue row is about: the job for an application, the portal for a connection. */
export function subjectOf(item: Pick<LiveItem, 'kind' | 'jobTitle' | 'companyName' | 'portalKey' | 'url'>): { title: string; detail: string } {
  if (item.kind === 'portal') {
    return { title: `Connecting ${item.portalKey ?? 'a job portal'}`, detail: hostOf(item.url) }
  }
  if (item.kind === 'practice') {
    return { title: 'Practice check', detail: hostOf(item.url) }
  }
  return {
    title: item.jobTitle ?? 'An application',
    detail: [item.companyName, hostOf(item.url)].filter(Boolean).join(' · '),
  }
}

/** Re-render once a second while `active`. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [active])
  return now
}

export function useLiveQueue() {
  const query = useQuery({
    queryKey: LIVE_KEY,
    queryFn: adminApi.live,
    refetchInterval: POLL_MS,
    // A check can arrive while this tab is in the background: keep asking so the alert can fire.
    refetchIntervalInBackground: true,
    staleTime: 0,
  })
  const raw = query.data
  const now = useNow(Boolean(raw?.length))
  const items = useMemo<LiveItem[]>(() => {
    if (!raw) return []
    const elapsed = Math.max(0, Math.floor((now - query.dataUpdatedAt) / 1000))
    return raw.map((item) => ({
      ...item,
      waitedSeconds: item.waitedSeconds + elapsed,
      remainingSeconds: item.remainingSeconds === null ? null : Math.max(0, item.remainingSeconds - elapsed),
    }))
  }, [raw, now, query.dataUpdatedAt])
  return { items, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch }
}
